import { useState } from "react";
import { useApp } from "@/App";
import { api } from "@/lib/api";
import { useFetch } from "@/lib/hooks";
import { CATEGORY_LABELS, CONDITION_LABELS, type ItemCategory, type ItemCondition } from "@/lib/types";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Package, Plus, CheckCircle, X } from "lucide-react";

export function ListItemPage() {
	const { navigate, currentUserId } = useApp();
	const { data: currentUser } = useFetch(() => api.users.get(currentUserId), [currentUserId]);

	const [title, setTitle] = useState("");
	const [description, setDescription] = useState("");
	const [category, setCategory] = useState<ItemCategory | "">("");
	const [condition, setCondition] = useState<ItemCondition | "">("");
	const [estimatedValue, setEstimatedValue] = useState("");
	const [tagInput, setTagInput] = useState("");
	const [tags, setTags] = useState<string[]>([]);
	const [wantedCategories, setWantedCategories] = useState<Set<ItemCategory>>(new Set());
	const [submitting, setSubmitting] = useState(false);
	const [submitted, setSubmitted] = useState(false);

	const addTag = () => {
		const tag = tagInput.trim().toLowerCase().replace(/\s+/g, "-");
		if (tag && !tags.includes(tag)) {
			setTags([...tags, tag]);
			setTagInput("");
		}
	};

	const removeTag = (tag: string) => {
		setTags(tags.filter((t) => t !== tag));
	};

	const toggleWantedCategory = (cat: ItemCategory) => {
		setWantedCategories((prev) => {
			const next = new Set(prev);
			if (next.has(cat)) next.delete(cat);
			else next.add(cat);
			return next;
		});
	};

	const isValid = title && description && category && condition && estimatedValue;

	const handleSubmit = async () => {
		if (!isValid || !category || !condition) return;
		setSubmitting(true);
		try {
			await api.items.create({
				ownerId: currentUserId,
				ownerUsername: currentUser?.username || "",
				title,
				description,
				category,
				condition,
				estimatedValue: Number(estimatedValue),
				images: [],
				tags,
				wantedCategories: Array.from(wantedCategories),
			});
			setSubmitted(true);
		} finally {
			setSubmitting(false);
		}
	};

	if (submitted) {
		return (
			<div className="max-w-lg mx-auto text-center py-16 space-y-4">
				<div className="h-16 w-16 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto">
					<CheckCircle className="h-8 w-8" />
				</div>
				<h2 className="text-2xl font-bold">Item Listed!</h2>
				<p className="text-muted-foreground">
					Your item is now visible to other traders on SwapChain.
				</p>
				<div className="flex gap-3 justify-center pt-4">
					<Button variant="outline" onClick={() => navigate("/profile")}>View Profile</Button>
					<Button onClick={() => navigate("/browse")}>Browse Items</Button>
				</div>
			</div>
		);
	}

	return (
		<div className="max-w-2xl mx-auto space-y-6">
			<div>
				<h1 className="text-3xl font-bold tracking-tight">List an Item</h1>
				<p className="text-muted-foreground mt-1">
					Add an item you'd like to trade on SwapChain.
				</p>
			</div>

			<Card>
				<CardHeader>
					<CardTitle className="text-base flex items-center gap-2">
						<Package className="h-4 w-4" />
						Item Details
					</CardTitle>
				</CardHeader>
				<CardContent className="space-y-4">
					<div className="space-y-2">
						<Label>Title</Label>
						<Input
							placeholder="What are you trading?"
							value={title}
							onChange={(e) => setTitle(e.target.value)}
						/>
					</div>

					<div className="space-y-2">
						<Label>Description</Label>
						<Textarea
							placeholder="Describe the item, its features, and any relevant details..."
							value={description}
							onChange={(e) => setDescription(e.target.value)}
							rows={4}
						/>
					</div>

					<div className="grid grid-cols-2 gap-4">
						<div className="space-y-2">
							<Label>Category</Label>
							<Select value={category} onValueChange={(v) => setCategory(v as ItemCategory)}>
								<SelectTrigger>
									<SelectValue placeholder="Select..." />
								</SelectTrigger>
								<SelectContent>
									{(Object.entries(CATEGORY_LABELS) as [ItemCategory, string][]).map(([key, label]) => (
										<SelectItem key={key} value={key}>{label}</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
						<div className="space-y-2">
							<Label>Condition</Label>
							<Select value={condition} onValueChange={(v) => setCondition(v as ItemCondition)}>
								<SelectTrigger>
									<SelectValue placeholder="Select..." />
								</SelectTrigger>
								<SelectContent>
									{(Object.entries(CONDITION_LABELS) as [ItemCondition, string][]).map(([key, label]) => (
										<SelectItem key={key} value={key}>{label}</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
					</div>

					<div className="space-y-2">
						<Label>Estimated Value ($)</Label>
						<Input
							type="number"
							placeholder="0"
							value={estimatedValue}
							onChange={(e) => setEstimatedValue(e.target.value)}
							min={0}
						/>
						<p className="text-[10px] text-muted-foreground">
							This helps our fairness algorithm evaluate swap proposals.
						</p>
					</div>

					<div className="space-y-2">
						<Label>Tags</Label>
						<div className="flex gap-2">
							<Input
								placeholder="Add a tag..."
								value={tagInput}
								onChange={(e) => setTagInput(e.target.value)}
								onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addTag())}
							/>
							<Button type="button" variant="outline" size="icon" onClick={addTag}>
								<Plus className="h-4 w-4" />
							</Button>
						</div>
						{tags.length > 0 && (
							<div className="flex flex-wrap gap-1.5 mt-2">
								{tags.map((tag) => (
									<Badge key={tag} variant="secondary" className="gap-1">
										{tag}
										<button onClick={() => removeTag(tag)}>
											<X className="h-3 w-3" />
										</button>
									</Badge>
								))}
							</div>
						)}
					</div>

					<div className="space-y-2">
						<Label>What would you trade for?</Label>
						<p className="text-xs text-muted-foreground">Select categories you're interested in receiving.</p>
						<div className="flex flex-wrap gap-1.5">
							{(Object.entries(CATEGORY_LABELS) as [ItemCategory, string][]).map(([key, label]) => (
								<Badge
									key={key}
									variant={wantedCategories.has(key) ? "default" : "outline"}
									className="cursor-pointer"
									onClick={() => toggleWantedCategory(key)}
								>
									{label}
								</Badge>
							))}
						</div>
					</div>

					<Button
						size="lg"
						className="w-full mt-4"
						disabled={!isValid || submitting}
						onClick={handleSubmit}
					>
						{submitting ? "Listing..." : "List Item"}
					</Button>
				</CardContent>
			</Card>
		</div>
	);
}
